import { DocumentCategory, Product } from '../types';
import { INITIAL_CATEGORIES } from './categoriesService';
import { INITIAL_COUNTRIES, CountryModel, filterProductsByCountryModel } from './countriesService';
import { slugify } from './slugBase';

export { slugify };

// Category slug mapping
export const CATEGORY_SLUG_MAP: Record<string, DocumentCategory> = {
  'passport': 'Passport',
  'id-card': 'ID Card',
  'driving-license': 'Driving License',
  'certificate': 'Certificate',
  'utility-bill': 'Utility Bill',
  'bank-statement': 'Bank Statement',
  'resume': 'Resume',
  'visa': 'Visa Documents',
  'visa-documents': 'Visa Documents',
  'health-id': 'Health ID',
  'residence-permit': 'Residence Permit',
  'others': 'Others',
};

export function getCategoryBySlug(slug: string): DocumentCategory | null {
  const normalized = slug.toLowerCase().trim();
  if (CATEGORY_SLUG_MAP[normalized]) {
    return CATEGORY_SLUG_MAP[normalized];
  }
  // Fallback search in INITIAL_CATEGORIES
  const found = INITIAL_CATEGORIES.find(c => slugify(c.name) === normalized);
  return found ? (found.name as DocumentCategory) : null;
}

export function getCategorySlug(categoryName: string): string {
  return slugify(categoryName);
}

// Country slug mapping
export function getCountryBySlug(slug: string, countriesList?: CountryModel[]): string | null {
  const normalized = slug.toLowerCase().trim();
  const list = (countriesList && countriesList.length > 0 ? countriesList : INITIAL_COUNTRIES).filter(c => c && typeof c === 'object');
  const found = list.find(c => 
    (c.slug && c.slug === normalized) || 
    (c.name && slugify(c.name) === normalized) || 
    (c.iso_code && c.iso_code.toLowerCase() === normalized)
  );
  return found ? found.name : null;
}

export function getCountrySlug(countryName: string): string {
  return slugify(countryName);
}

export function filterProductsByCountry(products: Product[], countryName: string, countriesList?: CountryModel[]): Product[] {
  const list = (countriesList && countriesList.length > 0 ? countriesList : INITIAL_COUNTRIES).filter(c => c && typeof c === 'object');
  const matchCountry = list.find(c => (c.name && c.name.toLowerCase() === countryName.toLowerCase()) || (c.slug && c.slug === slugify(countryName)));
  if (matchCountry) {
    return filterProductsByCountryModel(products, matchCountry);
  }
  return filterProductsByCountryModel(products, countryName);
}

// Product slug helper
export function getProductSlug(product: Product): string {
  return slugify(product.title);
}

export function findProductBySlug(products: Product[], slugOrId: string): Product | null {
  if (!products || products.length === 0 || !slugOrId) return null;

  const rawNormalized = slugOrId.toLowerCase().trim();

  // Standardize common variant terms (e.g. licence/license, driver/driving)
  const normalizeVariantText = (str: string) => {
    return str
      .toLowerCase()
      .trim()
      .replace(/licence/g, 'license')
      .replace(/driver/g, 'driving');
  };

  const normalized = normalizeVariantText(rawNormalized);

  // 1. Match exact ID
  const matchId = products.find(p => p.id.toLowerCase() === rawNormalized || normalizeVariantText(p.id) === normalized);
  if (matchId) return matchId;

  // 2. Match exact title slug
  const matchSlug = products.find(p => {
    const pSlug = slugify(p.title);
    return pSlug === rawNormalized || normalizeVariantText(pSlug) === normalized;
  });
  if (matchSlug) return matchSlug;

  // 3. Substring match
  const matchSub = products.find(p => {
    const pSlugNorm = normalizeVariantText(slugify(p.title));
    return pSlugNorm.includes(normalized) || normalized.includes(pSlugNorm);
  });
  if (matchSub) return matchSub;

  // 4. Token overlap matching (fuzzy keyword score)
  const targetTokens = normalized.split(/[^a-z0-9]+/).filter(t => t.length > 2);
  if (targetTokens.length > 0) {
    let bestProduct: Product | null = null;
    let maxScore = 0;

    for (const p of products) {
      const pTitleNorm = normalizeVariantText(p.title);
      const pTokens = pTitleNorm.split(/[^a-z0-9]+/).filter(t => t.length > 2);
      let score = 0; if (targetTokens[0] && (pTokens.includes(targetTokens[0]) || pTitleNorm.includes(targetTokens[0]))) { score += 50; }
      for (const token of targetTokens) {
        if (pTokens.includes(token)) score += 2;
        else if (pTitleNorm.includes(token)) score += 1;
      }
      if (score > maxScore) {
        maxScore = score;
        bestProduct = p;
      }
    }

    if (bestProduct && maxScore >= 50) {
      return bestProduct;
    }
  }

  return null;
}
